"use client";

import React, { useState } from "react";
import { X, ShieldCheck, FileText, Lock, HeartHandshake, CheckCircle } from "lucide-react";

export type LegalDocType = "privacy" | "terms" | "safeguarding" | "copyright" | null;

interface LegalModalsProps {
  activeDoc: LegalDocType;
  onClose: () => void;
}

export function LegalModals({ activeDoc, onClose }: LegalModalsProps) {
  if (!activeDoc) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      <div className="relative w-full max-w-3xl bg-[#0F172A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-10 my-8 text-slate-100 max-h-[85vh] flex flex-col">
        {/* Top Accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-amber-500 via-cyan-400 to-emerald-400" />

        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between shrink-0 bg-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
              {activeDoc === "privacy" && <Lock className="w-5 h-5" />}
              {activeDoc === "terms" && <FileText className="w-5 h-5" />}
              {activeDoc === "safeguarding" && <ShieldCheck className="w-5 h-5" />}
              {activeDoc === "copyright" && <HeartHandshake className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="font-outfit text-xl font-bold text-white">
                {activeDoc === "privacy" && "Privacy Policy & Minor Data Protection"}
                {activeDoc === "terms" && "Terms of Service & Campus Liability Framework"}
                {activeDoc === "safeguarding" && "Child Safeguarding & POCSO Compliance Policy"}
                {activeDoc === "copyright" && "Copyright & Intellectual Property Notice"}
              </h3>
              <p className="text-xs font-mono text-slate-400">
                Olympio Pvt. Ltd. · Last Updated: September 2026 · DPDP Act 2023 Compliant
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 sm:p-8 overflow-y-auto space-y-6 text-sm text-slate-300 leading-relaxed font-sans">
          {activeDoc === "privacy" && (
            <>
              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-mono flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  <strong>DPDP Act 2023 Certified:</strong> Olympio strictly complies with the Digital Personal Data Protection Act, 2023 of India regarding processing of personal data of children, requiring verifiable parental consent.
                </span>
              </div>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">1. Scope and Commitment</h4>
                <p>
                  Olympio Pvt. Ltd. (&ldquo;Olympio&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;) operates the Olympio Platform, providing school physical education management, youth athletic biometric assessments, and campus facility monetization. This policy explains how we collect, store, and safeguard data from schools, administrators, teachers, parents, students, and community marketplace users.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">2. Minor Data &amp; Parental Consent Protocol</h4>
                <p>
                  Under Section 9 of the DPDP Act 2023, personal and biometric data of minors (under 18 years of age) is processed solely for educational, physical development, and athletic benchmarking purposes.
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-300">
                  <li><strong>Verifiable Consent:</strong> Parents/guardians must authorize athletic testing via the DPDP Parental Consent Portal.</li>
                  <li><strong>No Behavioral Tracking:</strong> We never engage in targeted commercial advertising, behavioral profiling, or tracking directed at children.</li>
                  <li><strong>Anonymized Benchmarking:</strong> School-wide and national scouting percentiles are strictly aggregated without exposing identifiable personal coordinates to third parties.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">3. Biometric &amp; Health Data Architecture</h4>
                <p>
                  Physical measurements (standing height, sitting height, 30m sprint latency, VO2 max, Sit-and-Reach) and Pre-Participation Physical Evaluations (AAP PPE) are encrypted in transit via TLS 1.3 and at rest with AES-256. Database records enforce strict multi-tenant Row-Level Security (RLS) isolating each school campus.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">4. Data Localization &amp; Rights</h4>
                <p>
                  All institutional datasets are hosted on secure enterprise cloud infrastructure within the sovereign borders of India. Parents retain the statutory right to access, rectify, and request permanent erasure of their child&apos;s physical health records upon written application.
                </p>
              </section>
            </>
          )}

          {activeDoc === "terms" && (
            <>
              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">1. Acceptance of Terms</h4>
                <p>
                  By deploying Olympio or booking sports facilities through Olympio.in, partner institutions and community users agree to be bound by these Terms of Service.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">2. Campus Priority &amp; Academic Shield</h4>
                <p>
                  Intra-school athletic training, curriculum physical education, and institutional sporting events maintain absolute priority over after-hours commercial rentals. The school reserves the right to override community slots in the event of unscheduled academic requirements.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">3. Cryptographic Gate Passes &amp; Campus Access</h4>
                <p>
                  Entry to school grounds during after-hours rental is granted strictly upon verification of an HMAC-SHA256 cryptographically signed gate ticket at the campus security gate. Re-entry, ticket sharing, or unauthorized guests are strictly prohibited.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">4. Property Care &amp; Security Deposit Escrow</h4>
                <p>
                  All community bookings require pre-authorized security deposits. In the event of documented damage logged during post-use digital inspection by security proctors, the deposit is frozen to reimburse the school for repairs.
                </p>
              </section>
            </>
          )}

          {activeDoc === "safeguarding" && (
            <>
              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">1. Child Protection &amp; POCSO Standards</h4>
                <p>
                  Olympio maintains zero tolerance for any behavior endangering minors. The platform strictly enforces child safeguarding protocols aligned with the Protection of Children from Sexual Offences (POCSO) Act and international child protection standards.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">2. Physical Separation &amp; Security Shielding</h4>
                <p>
                  After-hours facility monetization operates on strict time separation. External community players are never permitted on school premises during hours when students are present. Security guard consoles enforce real-time check-ins and time-stamped exits.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">3. Medical Safety Gate Enforcement</h4>
                <p>
                  No student is permitted to participate in maximal anaerobic or high-impact testing without a certified school physician approving their Pre-Participation Physical Evaluation (AAP PPE).
                </p>
              </section>
            </>
          )}

          {activeDoc === "copyright" && (
            <>
              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">1. Proprietary Algorithms &amp; Intellectual Property</h4>
                <p>
                  The Olympio platform, including its proprietary Mirwald Somatic Maturity implementation, Léger VO2 max regression pipeline, 6-axis Talent Suitability Index (TSI) algorithms, cryptographic gate pass architecture, and Google Stitch design tokens, is the exclusive intellectual property of <strong>Olympio Pvt. Ltd.</strong>
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">2. Trademark Notice</h4>
                <p>
                  &ldquo;OLYMPIO&rdquo;, &ldquo;Race to Olympics&rdquo;, &ldquo;Athlete Passport&rdquo;, and the Olympio metallic crest are trademarks and service marks of Olympio Pvt. Ltd. registered or pending registration in India and other jurisdictions.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-bold text-white text-base">3. Corporate Entity</h4>
                <p className="font-mono text-xs bg-slate-900 p-3 rounded-lg border border-white/5">
                  Olympio Pvt. Ltd.<br />
                  T35 Nirala Estate, Techzone 4, Greater Noida West,<br />
                  Gautam Buddha Nagar, Noida, Uttar Pradesh, India - 201308<br />
                  CIN / Reg: Registered Private Limited Company · India<br />
                  Email: tiwnilesh1998@gmail.com · contact@olympio.in
                </p>
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-slate-900/60 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition-colors"
          >
            I Understand &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
}
